import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './FormComponent.css';
import './MenuSection.css';
import logo from '../reshipes-logo.jpg';
import ItemModal from './ItemModal';
import CartDrawer from './CartDrawer';
import { formatMenuTitle } from '../utils/menuTitle';
import PaymentInfo from './PaymentInfo';
import { TURNSTILE_SITE_KEY } from './Turnstile';

const BakeryOrderForm = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [openMenus, setOpenMenus] = useState(null); // null = loading
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '', email: '', phone: '',
    address1: '', address2: '', city: '', state: '', zip: '', otherState: ''
  });
  const navigate = useNavigate();

  const fetchProducts = async (menuName) => {
    try {
      const url = menuName
        ? `https://api.reshipesbakery.com/item/getItemsWithSpecificMenu?menuName=${encodeURIComponent(menuName)}`
        : 'https://api.reshipesbakery.com/item/getItemsWithSpecificMenu';
      const res = await fetch(url);
      if (!res.ok) throw new Error();
      setProducts(await res.json());
    } catch {
      console.error('Error fetching products');
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetch('https://api.reshipesbakery.com/item/getOpenMenus');
        if (!res.ok) throw new Error();
        const menus = await res.json();
        setOpenMenus(menus);
        if (menus.length === 0) {
          navigate('/form-closed');
        } else if (menus.length === 1) {
          setSelectedMenu(menus[0]);
          fetchProducts(menus[0]);
        }
        // if > 1, wait for user to pick
      } catch {
        console.error('Error checking form status');
        setOpenMenus([]);
        navigate('/form-closed');
      }
    };
    init();
  }, []);

  const handleCustomerInfoChange = e => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleAddToCart = itemWithQty => {
    setCart(prev => {
      const existing = prev.find(i => i.id === itemWithQty.id);
      if (existing) {
        return prev.map(i => i.id === itemWithQty.id ? { ...i, quantity: itemWithQty.quantity } : i);
      }
      return [...prev, itemWithQty];
    });
  };

  const handleUpdateQty = (id, newQty) => {
    setCart(prev => prev.map(i => i.id === id ? { ...i, quantity: newQty } : i));
  };

  const handleRemove = id => {
    setCart(prev => prev.filter(i => i.id !== id));
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const deliveryFee = (customerInfo.state === 'NY' || customerInfo.state === 'Other') ? 10 : 0;

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    const requestData = {
      customerInfo: {
        name: customerInfo.name,
        email: customerInfo.email,
        phoneNumber: customerInfo.phone,
        streetAddress: customerInfo.address1,
        aptNumber: customerInfo.address2,
        city: customerInfo.city,
        state: customerInfo.state,
        zipCode: customerInfo.zip,
      },
      orderItems: cart.map(i => ({ quantity: i.quantity, itemID: i.id })),
      comments,
      website: honeypot,
      turnstileToken,
    };

    try {
      const res = await fetch('https://api.reshipesbakery.com/order/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const msg = body?.detail || 'Failed to submit order. Please check your information and try again.';
        setSubmitError(msg);
        return;
      }
      const orderResult = await res.json();
      navigate('/order-summary', {
        state: { items: orderResult.items, total: orderResult.total, deliveryFee: orderResult.deliveryFee },
      });
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      setTurnstileResetKey(k => k + 1);
    }
  };

  const groupedProducts = products.reduce((groups, product) => {
    const cat = product.category || 'Uncategorized';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(product);
    return groups;
  }, {});

  const categories = Object.keys(groupedProducts);
  const currentCategory = activeCategory && categories.includes(activeCategory)
    ? activeCategory
    : categories[0] ?? null;

  const handleMenuPick = (menuName) => {
    setSelectedMenu(menuName);
    setProducts([]);
    setCart([]);
    fetchProducts(menuName);
  };

  const handleChangeMenu = () => {
    setSelectedMenu(null);
    setProducts([]);
    setCart([]);
  };

  if (openMenus === null) return <div>Loading…</div>;

  if (openMenus.length > 1 && !selectedMenu) {
    return (
      <div className="page-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 24 }}>
        <img src={logo} alt="Reshipes Logo" className="form-logo" />
        <h2 style={{ color: '#3b2410', textAlign: 'center' }}>Which menu would you like to order from?</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 360 }}>
          {openMenus.map(menu => (
            <button
              key={menu}
              onClick={() => handleMenuPick(menu)}
              style={{
                padding: '16px 24px',
                fontSize: '1.05rem',
                fontWeight: 600,
                background: '#c4762a',
                color: '#fff',
                border: 'none',
                borderRadius: 50,
                cursor: 'pointer',
              }}
            >
              {menu}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {/* Honeypot */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input type="text" id="website" name="website" value={honeypot}
          onChange={e => setHoneypot(e.target.value)} tabIndex="-1" autoComplete="off" />
      </div>

      <div className="form-header">
        <img src={logo} alt="Reshipes Logo" className="form-logo" />
        <h1>Reshipes Order Form</h1>
        {selectedMenu && (
          <p className="active-menu-banner">
            {formatMenuTitle(selectedMenu)}
            {openMenus.length > 1 && (
              <button
                className="active-menu-change"
                onClick={handleChangeMenu}
              >
                Change
              </button>
            )}
          </p>
        )}
      </div>

      <div className="form-section" style={{ textAlign: 'center', fontStyle: 'italic', color: '#5a4030' }}>
        <p>For all special orders please email us at <a href="mailto:reshipesbakery@gmail.com">reshipesbakery@gmail.com</a> or contact us at <a href="tel:+12019067541">201-906-7541</a>.</p>
        <PaymentInfo />
      </div>

      {/* Customer Info */}
      <div className="form-section">
        <h3>Customer Information</h3>
        <div className="field-row">
          <div className="field-group">
            <label htmlFor="name">Name</label>
            <input type="text" id="name" name="name" value={customerInfo.name} onChange={handleCustomerInfoChange} required />
          </div>
          <div className="field-group">
            <label htmlFor="email">Email</label>
            <input type="email" id="email" name="email" value={customerInfo.email} onChange={handleCustomerInfoChange} required />
          </div>
          <div className="field-group">
            <label htmlFor="phone">Phone</label>
            <input type="tel" id="phone" name="phone" value={customerInfo.phone} onChange={handleCustomerInfoChange} required />
          </div>
        </div>
        <div className="field-row-2">
          <div className="field-group">
            <label htmlFor="address1">Street Address</label>
            <input type="text" id="address1" name="address1" value={customerInfo.address1} onChange={handleCustomerInfoChange} required />
          </div>
          <div className="field-group">
            <label htmlFor="address2">Apt / Unit</label>
            <input type="text" id="address2" name="address2" value={customerInfo.address2} onChange={handleCustomerInfoChange} />
          </div>
        </div>
        <div className="field-row">
          <div className="field-group">
            <label htmlFor="city">City</label>
            <input type="text" id="city" name="city" value={customerInfo.city} onChange={handleCustomerInfoChange} required />
          </div>
          <div className="field-group">
            <label htmlFor="state">State</label>
            <select id="state" name="state" value={customerInfo.state} onChange={handleCustomerInfoChange} required>
              <option value="">Select a state</option>
              <option value="NY">New York</option>
              <option value="NJ">New Jersey</option>
              <option value="Other">Other</option>
            </select>
            {customerInfo.state === 'Other' && (
              <input type="text" className="other-state-input" id="otherState" name="otherState"
                placeholder="Please specify" value={customerInfo.otherState} onChange={handleCustomerInfoChange} required />
            )}
          </div>
          <div className="field-group">
            <label htmlFor="zip">Zip Code</label>
            <input type="text" id="zip" name="zip" value={customerInfo.zip} onChange={handleCustomerInfoChange} required />
          </div>
        </div>
      </div>

      {/* Menu */}
      <div className="form-section">
        <div className="menu-section-header">
          <h3 style={{ margin: 0 }}>Select Items</h3>
          <button className="cart-icon-btn" onClick={() => setDrawerOpen(true)}>
            <span className="cart-icon-svg">🛒</span>
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </button>
        </div>

        {products.length === 0 ? (
          <p>Loading products…</p>
        ) : (
          <>
            <div className="category-tabs">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`category-tab${cat === currentCategory ? ' active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                  {cart.some(i => groupedProducts[cat].find(p => p.id === i.id)) && (
                    <span className="category-tab-dot" />
                  )}
                </button>
              ))}
            </div>

            <div className="menu-category">
              {currentCategory && groupedProducts[currentCategory].map(product => {
                const inCart = cart.find(i => i.id === product.id);
                return (
                  <div
                    key={product.id}
                    className={`menu-item-row${inCart ? ' in-cart' : ''}`}
                    onClick={() => { if (!inCart) setSelectedItem(product); }}
                  >
                    {product.imageUrl && (
                      <img src={product.imageUrl} alt={product.foodName} className="menu-item-img" />
                    )}
                    <div className="menu-item-details">
                      <span className="menu-item-name">{product.size} {product.foodName}</span>
                    </div>
                    <span className="menu-item-price">${product.price.toFixed(2)}</span>
                    {inCart ? (
                      <div className="menu-item-cart-controls" onClick={e => e.stopPropagation()}>
                        <button
                          className="menu-qty-btn"
                          onClick={() => handleUpdateQty(product.id, inCart.quantity - 1)}
                          disabled={inCart.quantity <= inCart.minQty}
                        >–</button>
                        <span className="menu-qty-val">{inCart.quantity}</span>
                        <button
                          className="menu-qty-btn"
                          onClick={() => handleUpdateQty(product.id, inCart.quantity + 1)}
                          disabled={inCart.quantity >= inCart.maxQty}
                        >+</button>
                        <button
                          className="menu-remove-btn"
                          onClick={() => handleRemove(product.id)}
                        >×</button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Review Order button */}
      <div style={{ maxWidth: 860, margin: '0 auto 40px' }}>
        <button
          className="submit-btn"
          onClick={() => setDrawerOpen(true)}
          disabled={cart.length === 0}
        >
          Review Order {cartCount > 0 && `(${cartCount} item${cartCount !== 1 ? 's' : ''})`}
        </button>
      </div>

      {/* Item modal */}
      {selectedItem && (
        <ItemModal
          item={selectedItem}
          existingQty={cart.find(i => i.id === selectedItem.id)?.quantity}
          onClose={() => setSelectedItem(null)}
          onAddToCart={handleAddToCart}
        />
      )}

      {/* Cart drawer */}
      {drawerOpen && (
        <CartDrawer
          cart={cart}
          onClose={() => setDrawerOpen(false)}
          onUpdateQty={handleUpdateQty}
          onRemove={handleRemove}
          comments={comments}
          onCommentsChange={setComments}
          onSubmit={handleSubmit}
          deliveryFee={deliveryFee}
          submitting={submitting}
          error={submitError}
          onTurnstileToken={setTurnstileToken}
          turnstileReady={!TURNSTILE_SITE_KEY || Boolean(turnstileToken)}
          turnstileResetKey={turnstileResetKey}
        />
      )}
    </div>
  );
};

export default BakeryOrderForm;
